import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { botService } from '../services/bot'
import { BotState, Order } from '@/types'
import { formatPrice, formatAmount } from '@/utils/formatters'
import ConfirmModal from '@/components/ConfirmModal'

interface ManualOrder {
  tipo: 'limit' | 'market'
  lado: 'buy' | 'sell'
  cantidad: number
  precio?: number
}

const ControlPage: React.FC = () => {
  // Estados
  const [botState, setBotState] = useState<BotState | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState('')
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; action: string }>({ open: false, action: '' })

  // Estados para orden manual
  const [showOrderForm, setShowOrderForm] = useState(false)
  const [manualOrder, setManualOrder] = useState<ManualOrder>({
    tipo: 'limit',
    lado: 'buy',
    cantidad: 0,
    precio: undefined
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [state, ordersData] = await Promise.all([
        botService.getBotState(),
        botService.getOrders()
      ])
      setBotState(state)
      setOrders(ordersData.ordenes)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleStartBot = async () => {
    setActionLoading('start')
    try {
      await botService.startBot()
      await loadData()
      toast.success('Bot iniciado exitosamente')
    } catch (error) {
      console.error('Error starting bot:', error)
      toast.error('Error al iniciar el bot')
    } finally {
      setActionLoading('')
    }
  }

  const handleStopBot = async () => {
    setConfirmModal({ open: true, action: 'stop' })
  }

  const confirmStopBot = async () => {
    setConfirmModal({ open: false, action: '' })
    setActionLoading('stop')
    try {
      await botService.stopBot()
      await loadData()
      toast.success('Bot detenido exitosamente')
    } catch (error) {
      console.error('Error stopping bot:', error)
      toast.error('Error al detener el bot')
    } finally {
      setActionLoading('')
    }
  }

  const handleExecuteCycle = async () => {
    setActionLoading('cycle')
    try {
      await botService.executeCycle()
      await loadData()
      toast.success('Ciclo ejecutado exitosamente')
    } catch (error) {
      console.error('Error executing cycle:', error)
      toast.error('Error al ejecutar el ciclo')
    } finally {
      setActionLoading('')
    }
  }

  const handleCreateManualOrder = async () => {
    if (manualOrder.cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0')
      return
    }

    if (manualOrder.tipo === 'limit' && (!manualOrder.precio || manualOrder.precio <= 0)) {
      toast.error('El precio debe ser mayor a 0 para órdenes limit')
      return
    }

    setActionLoading('order')
    try {
      await botService.createManualOrder(manualOrder)
      await loadData()
      setShowOrderForm(false)
      setManualOrder({
        tipo: 'limit',
        lado: 'buy',
        cantidad: 0,
        precio: undefined
      })
      toast.success('Orden creada exitosamente')
    } catch (error) {
      console.error('Error creating order:', error)
      toast.error('Error al crear la orden')
    } finally {
      setActionLoading('')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">🎮 Panel de Control del Bot</h1>
          <p className="text-sm text-gray-600 mt-1">Controla el bot y gestiona órdenes manuales</p>
        </div>
      </div>

      {/* Estado del Bot y Controles */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estado del Bot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">📊 Estado Actual</h2>
          </div>
          <div className="p-6">
            {botState && (
              <div className="space-y-4">
                {/* Estado principal */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Estado del Bot:</span>
                  <div className="flex items-center">
                    <span className={`h-3 w-3 rounded-full mr-2 ${
                      botState.activo ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                    }`}></span>
                    <span className={`font-bold text-lg ${
                      botState.activo ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {botState.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </div>
                </div>

                {/* Modo */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Modo:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    botState.modo_dry_run 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {botState.modo_dry_run ? '🔸 SIMULACIÓN' : '💰 REAL'}
                  </span>
                </div>

                {/* Exchange */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Exchange:</span>
                  <span className="text-sm font-bold text-blue-600 uppercase">{botState.exchange}</span>
                </div>

                {/* Par */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">Par de Trading:</span>
                  <span className="text-sm font-bold text-purple-600">{botState.par}</span>
                </div>

                {/* Precio actual */}
                {botState.precio_actual && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Precio Actual:</span>
                    <span className="text-lg font-bold text-green-600">
                      {formatPrice(botState.precio_actual, 4)}
                    </span>
                  </div>
                )}

                {/* Capital */}
                {botState.capital_disponible && (
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Capital Disponible:</span>
                    <span className="text-lg font-bold text-indigo-600">
                      {formatPrice(botState.capital_disponible, 2)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Controles del Bot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">🎛️ Controles</h2>
          </div>
          <div className="p-6 space-y-4">
            {/* Botón Iniciar/Detener */}
            {botState && !botState.activo ? (
              <button
                onClick={handleStartBot}
                disabled={actionLoading === 'start'}
                className="w-full bg-green-600 text-white py-4 px-6 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 font-medium text-lg transition-colors"
              >
                {actionLoading === 'start' ? '⏳ Iniciando...' : '▶️ Iniciar Bot'}
              </button>
            ) : (
              <button
                onClick={handleStopBot}
                disabled={actionLoading === 'stop'}
                className="w-full bg-red-600 text-white py-4 px-6 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 font-medium text-lg transition-colors"
              >
                {actionLoading === 'stop' ? '⏳ Deteniendo...' : '⏸️ Detener Bot'}
              </button>
            )}

            {/* Botón Ejecutar Ciclo */}
            <button
              onClick={handleExecuteCycle}
              disabled={actionLoading === 'cycle' || !botState?.activo}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-medium transition-colors"
            >
              {actionLoading === 'cycle' ? '⏳ Ejecutando...' : '🔄 Ejecutar Ciclo Manual'}
            </button>

            {/* Botón Actualizar */}
            <button
              onClick={loadData}
              className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium transition-colors"
            >
              🔄 Actualizar Estado
            </button>

            {/* Información */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-blue-500">ℹ️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Controles del Bot
                  </h3>
                  <ul className="mt-2 text-sm text-blue-700 space-y-1">
                    <li>• <strong>Iniciar:</strong> Activa el bot y comienza el trading automático</li>
                    <li>• <strong>Detener:</strong> Pausa el bot (mantiene órdenes activas)</li>
                    <li>• <strong>Ejecutar Ciclo:</strong> Ejecuta una iteración manual del algoritmo</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas Rápidas */}
      {botState && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">📈</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Posiciones Abiertas</p>
                <p className="text-2xl font-semibold text-gray-900">{botState.posiciones_abiertas}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">⏱️</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Órdenes Pendientes</p>
                <p className="text-2xl font-semibold text-gray-900">{botState.ordenes_pendientes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">💎</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Ganancia Total</p>
                <p className={`text-2xl font-semibold ${
                  (botState.ganancia_total || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatPrice(botState.ganancia_total || 0, 2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gestión de Órdenes Manuales */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">📝 Órdenes Manuales</h2>
            <button
              onClick={() => setShowOrderForm(!showOrderForm)}
              disabled={!botState?.activo}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm disabled:opacity-50"
            >
              {showOrderForm ? 'Cancelar' : '+ Nueva Orden'}
            </button>
          </div>
        </div>
        <div className="p-6">
          {showOrderForm && (
            <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-md font-semibold text-gray-900 mb-4">Crear Orden Manual</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tipo de orden */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Orden
                  </label>
                  <select
                    value={manualOrder.tipo}
                    onChange={(e) => setManualOrder({...manualOrder, tipo: e.target.value as 'limit' | 'market'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="limit">Limit (con precio específico)</option>
                    <option value="market">Market (precio de mercado)</option>
                  </select>
                </div>

                {/* Lado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lado
                  </label>
                  <select
                    value={manualOrder.lado}
                    onChange={(e) => setManualOrder({...manualOrder, lado: e.target.value as 'buy' | 'sell'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="buy">📈 Comprar (Buy)</option>
                    <option value="sell">📉 Vender (Sell)</option>
                  </select>
                </div>

                {/* Cantidad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    value={manualOrder.cantidad}
                    onChange={(e) => setManualOrder({...manualOrder, cantidad: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.0"
                    step="0.000001"
                    min="0"
                  />
                </div>

                {/* Precio (solo para limit) */}
                {manualOrder.tipo === 'limit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Precio ($)
                    </label>
                    <input
                      type="number"
                      value={manualOrder.precio || ''}
                      onChange={(e) => setManualOrder({...manualOrder, precio: parseFloat(e.target.value) || undefined})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.0"
                      step="0.01"
                      min="0"
                    />
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-yellow-500">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Advertencia
                    </h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      Las órdenes manuales se ejecutarán independientemente de la estrategia del grid. 
                      Úsalas con precaución y verifica los valores antes de confirmar.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex space-x-3">
                <button
                  onClick={handleCreateManualOrder}
                  disabled={actionLoading === 'order'}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {actionLoading === 'order' ? '⏳ Creando...' : '✅ Crear Orden'}
                </button>
                <button
                  onClick={() => setShowOrderForm(false)}
                  className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Lista de órdenes activas */}
          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                        {order.id.substring(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 uppercase">
                        {order.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.side === 'buy' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.side === 'buy' ? '📈 BUY' : '📉 SELL'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatAmount(order.amount, 6)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                        {order.price ? formatPrice(order.price, 4) : 'Market'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.status === 'closed' 
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'open'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay órdenes activas</h3>
              <p className="text-gray-500">Las órdenes aparecerán aquí cuando se creen o ejecuten</p>
            </div>
          )}
        </div>
      </div>
    </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Detener Bot"
        message="¿Estás seguro de que quieres detener el bot?"
        confirmText="Detener"
        variant="danger"
        onConfirm={confirmStopBot}
        onCancel={() => setConfirmModal({ open: false, action: '' })}
      />
    </>
  )
}

export default ControlPage